/**
 * Pure mapping from the raw `edit_lock` envelope to an `EditLockSnapshot`.
 *
 * `meta` reports state without an `acquired` flag, so ownership is derived by
 * comparing `editing_user` against the current user's email. Comparison is
 * trimmed and case-insensitive because the server echoes whatever casing was
 * stored, while the client reads its own address from local storage.
 *
 * `isMine` describes who the server says holds the lock. It is not a permission
 * verdict: acquire and extend answer that explicitly via `acquired`.
 */
import type { EditLockResponse, EditLockSnapshot } from './types';

/** Below this, an epoch value must be seconds rather than milliseconds. */
const EPOCH_SECONDS_CEILING = 1e12;
const NUMERIC_ONLY = /^\d+$/;

const EMPTY_SNAPSHOT: EditLockSnapshot = {
    holder: null,
    isMine: false,
    isHeldByOther: false,
    expiresAtMs: null,
    startedAtMs: null,
    ttlSec: null,
};

export const normalizeEmail = (email: string | null | undefined): string =>
    typeof email === 'string' ? email.trim().toLowerCase() : '';

/** Same-user check that tolerates casing and padding differences. */
export const isSameUser = (a: string | null | undefined, b: string | null | undefined): boolean => {
    const left = normalizeEmail(a);
    return left !== '' && left === normalizeEmail(b);
};

/**
 * Accepts the ISO-8601 string the contract documents, and epoch seconds or
 * millis as a fallback, since the field has shipped in both shapes.
 */
export function parseStartedAt(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;

    if (typeof value === 'number' || NUMERIC_ONLY.test(String(value))) {
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric <= 0) return null;
        return numeric < EPOCH_SECONDS_CEILING ? numeric * 1000 : numeric;
    }

    const parsed = Date.parse(String(value));
    return Number.isNaN(parsed) ? null : parsed;
}

/** Coerces the TTL, rejecting non-numeric placeholders and non-positive values. */
export function parseTtlSeconds(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return null;
    return Math.floor(numeric);
}

/**
 * Normalizes any edit-lock response.
 *
 * `receivedAtMs` must be the client's `Date.now()` at the moment the response
 * arrived. Anchoring the deadline there means only elapsed local time is ever
 * measured, so absolute clock skew between client and server is irrelevant.
 */
export function toEditLockSnapshot(
    response: EditLockResponse | null | undefined,
    receivedAtMs: number,
    currentUserEmail: string | null,
): EditLockSnapshot {
    const lock = response?.edit_lock;
    const holderRaw = lock?.editing_user;
    const holder = typeof holderRaw === 'string' && holderRaw.trim() !== '' ? holderRaw.trim() : null;

    if (holder === null) return EMPTY_SNAPSHOT;

    const ttlSec = parseTtlSeconds(lock?.session_expires_in_sec);
    const isMine = isSameUser(holder, currentUserEmail);

    return {
        holder,
        isMine,
        isHeldByOther: !isMine,
        // A holder with no usable TTL is treated as having no live lease; the
        // next server round-trip is what decides, never a guessed deadline.
        expiresAtMs: ttlSec === null ? null : receivedAtMs + ttlSec * 1000,
        startedAtMs: parseStartedAt(lock?.started_at),
        ttlSec,
    };
}

export { EMPTY_SNAPSHOT };
